import { get } from 'svelte/store';
import { getDateByTimeZone } from 'src/helpers/date_helper';
import {
	buildChartRangeControls,
	type ChartControlsDataInterface,
} from 'src/helpers/statistics/ui/chart_range_helpers';
import { user_data } from 'src/stores/account/user';
import type { ChartStatisticRangePeriod } from 'src/types/Chart';

/** User-local "now" without i18n — safe at module init before locale is set. */
const nowInUserTimezone = (): Date => getDateByTimeZone(get(user_data)?.timezone);

export type EnergyMonitoringPeriodId = 'live' | ChartStatisticRangePeriod;

export type EnergyMonitoringPeriodOption = {
	id: EnergyMonitoringPeriodId;
	period: ChartStatisticRangePeriod;
	labelKey: string;
	labelDefault: string;
};

export const ENERGY_MONITORING_PERIODS: EnergyMonitoringPeriodOption[] = [
	{
		id: 'live',
		period: '24_hours',
		labelKey: 'label_live',
		labelDefault: 'Live',
	},
	{
		id: '24_hours',
		period: '24_hours',
		labelKey: 'option_24_hours',
		labelDefault: 'Last 24 hours',
	},
	{
		id: '7_days',
		period: '7_days',
		labelKey: 'option_7_days',
		labelDefault: 'Last 7 days',
	},
	{
		id: '30_days',
		period: '30_days',
		labelKey: 'option_30_days',
		labelDefault: 'Last 30 days',
	},
	{
		id: 'month',
		period: 'month',
		labelKey: 'energy_monitoring_this_month',
		labelDefault: 'This month',
	},
];

export const isEnergyLivePeriod = (periodId: EnergyMonitoringPeriodId): boolean => periodId === 'live';

export const buildEnergyRangeControls = (
	periodId: EnergyMonitoringPeriodId = 'live',
): ChartControlsDataInterface => {
	const option =
		ENERGY_MONITORING_PERIODS.find((p) => p.id === periodId) ?? ENERGY_MONITORING_PERIODS[0];
	const now = nowInUserTimezone();
	now.setSeconds(0, 0);
	const isRollingWindow =
		isEnergyLivePeriod(periodId) || periodId === '24_hours' || periodId === '30_days';
	return buildChartRangeControls(option.period, now, isRollingWindow);
};

export const refreshLiveRangeEnd = (
	controls: ChartControlsDataInterface,
	periodId: EnergyMonitoringPeriodId,
): ChartControlsDataInterface => {
	if (!isEnergyLivePeriod(periodId)) return controls;

	const now = nowInUserTimezone();
	now.setSeconds(0, 0);
	return {
		...controls,
		time: buildChartRangeControls('24_hours', now, true).time,
	};
};
